import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import CellarEdgeLogo from './CellarEdgeLogo';
import { color, font, radius, shadow } from '../theme';

const TopNav: React.FC = () => {
    const location = useLocation();

    const isActive = (path: string) => location.pathname === path;

    const styles = {
        topnav: {
            backgroundColor: color.surface,
            borderBottom: `1px solid ${color.border}`,
            padding: '0',
            boxShadow: shadow.sm,
            position: 'sticky' as const,
            top: 0,
            zIndex: 1000,
            backdropFilter: 'blur(8px)',
        },
        container: {
            maxWidth: '1400px', // Match ProductClaude container maxWidth
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 24px', // Match ProductClaude container padding
        },
        leftSection: {
            display: 'flex',
            alignItems: 'center',
            gap: '40px',
            marginLeft: '0', // Align with left edge of filter sidebar
        },
        rightSection: {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            marginRight: '0', // Remove any right margin to align with content
        },
        logo: {
            display: 'flex',
            alignItems: 'center',
            padding: '10px 0',
            textDecoration: 'none',
            transition: 'all 0.2s ease',
        },
        logoWrapper: {
            transition: 'transform 0.2s ease',
        },
        nav: {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
        },
        navList: {
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            listStyle: 'none',
            margin: 0,
            padding: 0,
        },
        navItem: (active: boolean) => ({
            display: 'flex',
            alignItems: 'center',
            backgroundColor: active ? color.claret[50] : 'transparent',
            borderRadius: radius.md,
            border: `1px solid ${active ? color.claret[100] : 'transparent'}`,
        }),
        navLink: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 18px',
            textDecoration: 'none',
            color: color.ink[500],
            fontSize: '14.5px',
            fontWeight: '500' as const,
            letterSpacing: '0.01em',
            borderRadius: radius.md,
            transition: 'all 0.15s ease',
            position: 'relative' as const,
            border: '1px solid transparent',
        },
        activeLink: {
            color: color.claret[700],
            fontWeight: '600' as const,
        },
    };

    return (
        <nav style={styles.topnav}>
            <div style={styles.container}>
                <div style={styles.leftSection}>
                    <CellarEdgeLogo />
                    <ul style={styles.navList}>
                        <li style={styles.navItem(isActive('/') || isActive('/master-products'))}>
                            <Link
                                to="/master-products"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/') || isActive('/master-products') ? styles.activeLink : {})
                                }}
                            >
                                Master Products
                            </Link>
                        </li>
                        <li style={styles.navItem(isActive('/alerts'))}>
                            <Link
                                to="/alerts"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/alerts') ? styles.activeLink : {})
                                }}
                            >
                                Alerts
                            </Link>
                        </li>
                        <li style={styles.navItem(isActive('/alert-rules'))}>
                            <Link
                                to="/alert-rules"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/alert-rules') ? styles.activeLink : {})
                                }}
                            >
                                Alert Rules
                            </Link>
                        </li>
                        <li style={styles.navItem(isActive('/data-sources'))}>
                            <Link
                                to="/data-sources"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/data-sources') ? styles.activeLink : {})
                                }}
                            >
                                Data Sources
                            </Link>
                        </li>
                        <li style={styles.navItem(isActive('/lwin'))}>
                            <Link
                                to="/lwin"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/lwin') ? styles.activeLink : {})
                                }}
                            >
                                LWIN References
                            </Link>
                        </li>
                        <li style={styles.navItem(isActive('/bbr-account'))}>
                            <Link
                                to="/bbr-account"
                                style={{
                                    ...styles.navLink,
                                    ...(isActive('/bbr-account') ? styles.activeLink : {})
                                }}
                            >
                                My Cellar
                            </Link>
                        </li>
                    </ul>
                </div>

                <div style={styles.rightSection}>
                    {/* This can be used for future right-aligned items like user menu */}
                </div>
            </div>
        </nav>
    );
};

export default TopNav;